(ns ductile.ui.components.omnibox
  (:refer-clojure :exclude [any?])
  (:require ["luxon" :as luxon :refer [DateTime]]
            ["react-dom" :as react-dom]
            [clojure.string :as str]
            [ductile.time.format :as time-format]
            [ductile.ui.components :as ui]
            [ductile.ui.locale :refer [tr]]
            [nextjournal.clerk.render.hooks :as hooks]
            [reagent.core :as r]))


(defn find-parent [node pred]
  (loop [node node]
    (cond
      (pred node) node
      (nil? node) nil
      :else (recur (.-parentNode node)))))

(defn child? [node parent]
  (boolean
   (find-parent node #(identical? % parent))))

(defn popup-popup [!ref !button-ref !button-rect markup]
  (r/with-let [!max-left (r/atom js/Number.MAX_SAFE_INTEGER)]
    (let [relayout! (fn [_]
                      (when-some [ref @!ref]
                        (reset! !max-left (- js/window.innerWidth (.-clientWidth ref)))))]
      (hooks/use-effect
       (fn []
         (relayout! nil)
         (js/window.addEventListener "resize" relayout!)
         #(js/window.removeEventListener "resize" relayout!)))
      [:div {:ref !ref
             :data-testid "omnibox-filter-popup"
             :class ["absolute" "z-20" "overflow-y-scroll" "rounded" "bg-white"
                     "shadow-lg" "border" "border-slate-300"]
             :style (let [button @!button-ref
                          rect   @!button-rect]
                      {:left       (min (.-left rect) (- @!max-left 20))
                       :top        (str "calc(" (+ (.-bottom rect) js/window.scrollY) "px + 0.25rem)")
                       :min-width  (.-offsetWidth button)
                       :max-height "50svh"})}
       markup])))

(defn popup [tick opts button-markup popup-markup]
  (r/with-let [!expanded-default (r/atom false)
               !button-rect      (r/atom nil)
               !portal-root      (atom nil)]
    (let [!button-ref (hooks/use-ref nil)
          !popup-ref  (hooks/use-ref nil)
          !expanded   (or (:!expanded opts) !expanded-default)
          {:keys [on-close]} opts]

      ;; close popup when clicking outside
      (hooks/use-effect
       (fn []
         (let [on-click (fn [event]
                          (cond
                            (child? (.-target event) @!button-ref)
                            (swap! !expanded not)

                            (and @!expanded
                                 (not (child? (.-target event) @!popup-ref)))
                            (do
                              (reset! !expanded false)
                              (when on-close
                                (on-close)))))]
           (js/document.addEventListener "click" on-click)
           #(js/document.removeEventListener "click" on-click))))

      ;; update absolute position
      (hooks/use-effect
       (fn []
         (let [containers (->> @!button-ref
                               (iterate #(.-parentNode %))
                               (take-while #(instance? js/Element %))
                               (filter #(let [style (js/window.getComputedStyle %)]
                                          (or
                                           (#{"scroll" "auto"} (.-overflowX style))
                                           (#{"scroll" "auto"} (.-overflowY style))))))
               callback (fn [_]
                          (reset! !button-rect (.getBoundingClientRect @!button-ref)))]
           (callback nil)
           (doseq [el containers]
             (.addEventListener el "scroll" callback))
           #(doseq [el containers]
              (.removeEventListener el "scroll" callback))))
       [@!expanded tick])
      [:<>
       [:div {:class (concat ["relative"] (:button-classes opts))
              :ref !button-ref}
        button-markup]
       (when @!expanded
         (react-dom/createPortal
          (r/as-element
           [popup-popup !popup-ref !button-ref !button-rect popup-markup])
          js/document.body))])))

(defn date->day [d]
  (when (instance? js/Date d)
    (.toJSDate (.startOf (.fromISO DateTime (.toISOString d)) "day"))))

(defmulti matches-filter? (fn [value filter] (:filter.omnibox/type filter)))

(defmethod matches-filter? :filter.omnibox.type/exact [value filter]
  (= (if (instance? js/Date value) (date->day value) value)
     (:filter.omnibox/normalized-value filter)))

(defmethod matches-filter? :filter.omnibox.type/substring [value filter]
  (and (not (instance? js/Date value))
       (some? (str/index-of value (:filter.omnibox/normalized-value filter)))))

(defmethod matches-filter? :filter.omnibox.type/glob [value filter]
  (and (not (instance? js/Date value))
       (some? (re-matches (:filter.omnibox/regex filter) value))))

(defmethod matches-filter? :filter.omnibox.type/range [value filter]
  (if (instance? js/Date value)
    (and
     (if-some [from (:filter.omnibox/from filter)]
       (>= value from)
       true)
     (if-some [to (:filter.omnibox/to filter)]
       (<= value to)
       true))
    (let [num (parse-double value)]
      (and
       (if-some [from (:filter.omnibox/from filter)]
         (<= from num)
         true)
       (if-some [to (:filter.omnibox/to filter)]
         (<= num to)
         true)))))

(defmethod matches-filter? :filter.omnibox.type/not-substring [value filter]
  (and (not (instance? js/Date value))
       (some? (str/index-of value (:filter.omnibox/normalized-value filter)))))

(defmethod matches-filter? :filter.omnibox.type/not-glob [value filter]
  (and (not (instance? js/Date value))
       (some? (re-matches (:filter.omnibox/regex filter) value))))

(defn any? [pred coll]
  (reduce
   #(when (pred %2) (reduced true))
   nil
   coll))

(defn normalize [value]
  (cond
    (instance? js/Date value) value
    (keyword? value) (str/lower-case (tr value))
    :else (str/lower-case (str value))))

(defn matches? [value filters]
  (let [normalized (normalize value)
        {:filter.omnibox.type/keys [exact substring glob range not-substring not-glob]} (group-by :filter.omnibox/type filters)]
    (and
     (or
      (every? empty? [exact substring glob range])
      (any? #(matches-filter? normalized %) exact)
      (any? #(matches-filter? normalized %) substring)
      (any? #(matches-filter? normalized %) glob)
      (any? #(matches-filter? normalized %) range))
     (or
      (every? empty? [not-substring not-glob])
      (not
       (or
        (any? #(matches-filter? normalized %) not-substring)
        (any? #(matches-filter? normalized %) not-glob)))))))

(defn glob-regex [glob]
  (try
    (as-> glob %
      (str/lower-case %)
      (str/replace % #"[\.\\\(\)\{\}\^\$\+\|]" "\\.")
      (str/replace % #"\[!" "[^")
      (str/replace % "*" ".*")
      (str/replace % "?" ".")
      (str "^" % "$")
      (js/RegExp. % "u"))
    (catch js/Error e
      nil)))

(defn text->numeric-filter [text]
  (let [text (str/trim text)]
    (condp re-matches text
      #"(\d+)\s*(-|\.\.\.?)\s*(\d+)" :>>
      (fn [[_ from _ to]]
        (let [from (parse-long from)
              to   (parse-long to)]
          {:filter.omnibox/type  :filter.omnibox.type/range
           :filter.omnibox/label text
           :filter.omnibox/from  from
           :filter.omnibox/to    to}))

      #"(?:>\s*(\d+)|(\d+)\s*(?:\+|\.\.\.?))" :>>
      (fn [[_ from1 from2]]
        (let [from (parse-long (or from1 from2))]
          {:filter.omnibox/type  :filter.omnibox.type/range
           :filter.omnibox/label text
           :filter.omnibox/from  from}))

      #"(?:<|\.\.\.?)\s*(\d+)" :>>
      (fn [[_ to]]
        (let [to (parse-long to)]
          {:filter.omnibox/type  :filter.omnibox.type/range
           :filter.omnibox/label text
           :filter.omnibox/to    to}))

      nil)))

(comment
  (re-matches #"(\d{1,2}\.\d{1,2}\.\d{2,4})-(\d{1,2}\.\d{1,2}\.\d{2,4})" "31.12.2024-1.6.2025")
  (re-matches #"(\d{1,2}\.\d{1,2}\.\d{4})" "31.12.2024")
  (re-matches #"(>|<)(\d{1,2}\.\d{1,2}\.\d{4})" "<31.12.2024"))

(defn parse-date [text]
  (.toJSDate (.fromFormat DateTime text "dd.MM.yy")))

(defn text->date-filter [text]
  (let [text (str/trim text)]
    (condp re-matches text
      #"(\d{1,2}\.\d{1,2}\.\d{2,4})-(\d{1,2}\.\d{1,2}\.\d{2,4})" :>>
      (fn [[_ from to]]
        (let [from (parse-date from)
              to   (parse-date to)]
          {:filter.omnibox/type  :filter.omnibox.type/range
           :filter.omnibox/label text
           :filter.omnibox/from  from
           :filter.omnibox/to    to}))

      #"(\d{1,2}\.\d{1,2}\.\d{2,4})(-)" :>>
      (fn [[_ from _]]
        (let [from (parse-date from)]
          {:filter.omnibox/type  :filter.omnibox.type/range
           :filter.omnibox/label text
           :filter.omnibox/from  from}))

      #"(-)(\d{1,2}\.\d{1,2}\.\d{2,4})" :>>
      (fn [[_ _ to]]
        (let [to (parse-date to)]
          {:filter.omnibox/type  :filter.omnibox.type/range
           :filter.omnibox/label text
           :filter.omnibox/to    to}))

      nil)))

(defn text->filter [text]
  (let [text (str/trim text)]
    (condp re-matches text
      #"-.*[\*\?\[].*"
      (let [text' (str/trim (subs text 1))]
        (when-some [re (glob-regex text')]
          {:filter.omnibox/type  :filter.omnibox.type/not-glob
           :filter.omnibox/label text'
           :filter.omnibox/regex re}))

      #"-.*[^\s].*"
      (let [text' (str/trim (subs text 1))]
        {:filter.omnibox/type             :filter.omnibox.type/not-substring
         :filter.omnibox/label            text'
         :filter.omnibox/normalized-value (str/lower-case text')})

      #".*[\*\?\[].*"
      (when-some [re (glob-regex text)]
        {:filter.omnibox/type  :filter.omnibox.type/glob
         :filter.omnibox/label text
         :filter.omnibox/regex re})

      #".*[^\s]+.*"
      {:filter.omnibox/type             :filter.omnibox.type/substring
       :filter.omnibox/label            text
       :filter.omnibox/normalized-value (normalize text)}

      nil)))

(defn date-label [d]
  (when (instance? js/Date d)
    (time-format/->str {:format :date :style :short} d)))

(defn filter-text [{:as props :filter.omnibox/keys [type custom-label label from to]}]
  (let [label (cond-> label
                (instance? js/Date label) date-label)]
    (or custom-label
        (case type
          :filter.omnibox.type/exact         (tr "Is “%1”" label)
          :filter.omnibox.type/substring     (tr "Contains “%1”" label)
          :filter.omnibox.type/glob          (tr "Matches “%1”" label)
          :filter.omnibox.type/range         (cond
                                               (and from to) (tr "Between “%1” and “%2”"
                                                                 (cond-> from
                                                                   (instance? js/Date from) date-label)
                                                                 (cond-> to
                                                                   (instance? js/Date to) date-label))
                                               from (if (instance? js/Date from)
                                                      (tr "After %1" (date-label from))
                                                      (tr "%1 or more" from))  ; % and more
                                               to   (if (instance? js/Date to)
                                                      (tr "Before %1" (date-label to))
                                                      (tr "%1 or less" to)))
          :filter.omnibox.type/not-substring (tr "Doesn’t contain “%1”" label)
          :filter.omnibox.type/not-glob      (tr "Doesn’t match “%1”" label)))))

(defn update-selected [opts delta]
  (when-some [dropdown (js/document.querySelector "#omnibox-dropdown")]
    (let [{:keys [!selected rows]} opts
          children  (.-childNodes dropdown)
          cnt       (count rows)
          selected' (swap! !selected
                           (fn [selected]
                             (let [selected (case delta
                                              1  (inc (or selected -1))
                                              -1 (dec (or selected cnt)))]
                               (when (<= 0 selected (dec cnt))
                                 selected))))]
      (when selected'
        (some-> dropdown
                (.querySelector (str ":nth-child(" (inc selected') ")"))
                (.scrollIntoView #js {:behavior "smooth", :block "nearest"}))))))

(defn on-row-click [e row opts]
  (let [{:row/keys [type the-filter label normalized-value checked? disabled?]} row
        {:keys [filters set-filters !text !expanded !selected]} opts]
    (when-not disabled?
      (case type
        :row.type/filter
        (do
          (.preventDefault e)
          (set-filters (conj filters the-filter))
          (reset! !text "")
          (reset! !expanded false)
          (reset! !selected nil))

        :row.type/choice
        (do
          (.preventDefault e)
          (when-not disabled?
            (if checked?
              (let [the-filter (->> filters
                                    (filter #(= :filter.omnibox.type/exact (:filter.omnibox/type %)))
                                    (filter #(= normalized-value (:filter.omnibox/normalized-value %))))]
                (set-filters (apply disj filters the-filter)))
              (let [the-filter {:filter.omnibox/type             :filter.omnibox.type/exact
                                :filter.omnibox/label            label
                                :filter.omnibox/normalized-value normalized-value}]
                (set-filters (conj filters the-filter))))))

        nil))))

(defn input [{:keys [rows choices filters set-filters !text !expanded !selected keyword? numeric? date? data-testid] :as opts}]
  [:input
   {:class ["w-full" "cursor-default" "rounded-[3px]" "px-[6px]"
            "ring-1" "ring-slate-300" "font-normal" "placeholder-slate-400"
            "focus:outline-none" "focus:ring-2" "focus:ring-blue-500" "sm:leading-6"
            "bg-white"]
    :type        "text"
    :placeholder "Filter..."
    :data-testid data-testid
    :value       @!text
    :on-change   #(do
                    (reset! !text (-> % .-currentTarget .-value))
                    (reset! !selected nil)
                    (compare-and-set! !expanded false true))
    :on-key-down (fn [e]
                   (case (.-key e)
                     "Enter"
                     (if-some [selected @!selected]
                       (on-row-click e (nth rows selected) opts)
                       (when-some [filter-from-text (or ; don't put text->filter in cond :else, always fall back to text filter when ->numeric returns nil (eg for filtering numeric by "2*")
                                                     (cond
                                                       (:text->filter opts) ((:text->filter opts) @!text)
                                                       numeric? (text->numeric-filter @!text)
                                                       date? (text->date-filter @!text)
                                                       keyword? (text->filter (name @!text)))
                                                     (text->filter @!text))]
                         (.preventDefault e)
                         (set-filters (conj filters filter-from-text))
                         (reset! !text "")
                         (reset! !expanded false)))

                     "Escape"
                     (do
                       (.preventDefault e)
                       (reset! !selected nil)
                       (reset! !text "")
                       (reset! !expanded false))

                     " "
                     (when-some [selected @!selected]
                       (on-row-click e (nth rows selected) opts))

                     "ArrowDown"
                     (do
                       (.preventDefault e)
                       (update-selected opts 1))

                     "ArrowUp"
                     (do
                       (.preventDefault e)
                       (update-selected opts -1))

                     nil))}])

(defn search [{:keys [set-filters] :as opts}]
  (r/with-let [!text (r/atom "")]
    [:div
     {:class ["flex" "flex-col" "gap-1"]}
     [:input
      {:class ["w-full" "cursor-default" "rounded-[3px]" "px-[6px]"
               "ring-1" "ring-slate-300" "font-normal" "placeholder-slate-400"
               "focus:outline-none" "focus:ring-2" "focus:ring-blue-500" "sm:leading-6"
               "bg-white"]
       :type "text"
       :placeholder "Search..."
       :value @!text
       :on-change #(let [val (-> % .-currentTarget .-value)]
                     (reset! !text val)
                     (set-filters val))}]]))

(defn selected-filters [{:keys [choices filters set-filters]}]
  [:<>
   (for [{:filter.omnibox/keys [type custom-label label tooltip] :as filter} filters
         :let [on-click (:on-click (ui/with-events filter))]]
     ^{:key (str type ":" (or custom-label label))}
     [:div.flex {:title tooltip}
      [:div
       {:class ["ring-1" "ring-slate-300" "bg-white" "text-[12px]" "px-[6px]" "py-[3px]" "rounded-[3px]" "group"
                (when on-click "cursor-pointer]")]
        :on-click on-click}
       (or
        custom-label
        (filter-text filter))

       (when on-click
         [:div.font-normal.font-sans {:class "text-[11px]"}
          [ui/link {:on-click #(.preventDefault %)
                    :class "group-hover"} (tr "Details")]])]
      [:button
       {:class ["px-[6px]" "py-[3px]" "rounded-l-[3px]" "text-slate-400" "hover:text-inherit"]
        :on-click (fn [_]
                    (set-filters (disj filters filter)))}
       "×"]])])

(defn checkbox [{:keys [checked? disabled? on-change]}]
  [:input.w-4.h-4.appearance-none.rounded.border
   {:type      :checkbox
    :checked   (boolean checked?)
    :disabled  (boolean disabled?)
    :on-change on-change
    :read-only true
    :class     (concat ["bg-contain" "bg-no-repeat"]
                       (case [(boolean checked?) (boolean disabled?)]
                         [true false]  ["border-blue-500"  "bg-blue-500"]
                         [false false] ["border-slate-300" "bg-white"]
                         [true true]   ["border-gray-200"  "bg-gray-100"]
                         [false true]  ["border-gray-200"  "bg-gray-100"]))
    :style     (when checked?
                 (let [fill (if disabled? "%23D1D5DB" "%23FFF")]
                   {:background-image (str "url(\"data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='" fill "' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e\")")}))}])

(defn icon-filter []
  [:svg {:viewBox "0 0 20 20"
         :fill "currentColor"
         :xmlns "http://www.w3.org/2000/svg"}
   [:path {:d "M18 1H2V3L10 13L18 3V1Z"}]
   [:path {:d "M8 5H12V18L8 15V5Z"}]])

(defn dropdown [{:keys [rows filters set-filters !text !expanded !selected keyword? numeric? date? font] :as opts}]
  [:ul.font-sans.py-1.text-base.not-prose
   {:id "omnibox-dropdown"
    :class ["focus:outline-none" "sm:text-sm" (cond
                                                numeric? "tabular-nums"
                                                (= :mono font) "font-mono"
                                                (= :tabular font) "tabular-nums")]}
   (doall
    (for [[row idx] (map vector rows (range))
          :let [{:row/keys [type the-filter label normalized-value checked? disabled?]} row]]
      ^{:key (str type ":" normalized-value)}
      [:li {:class (concat
                    ["cursor-default" "select-none" "flex" "px-2" "py-0.5"
                     "gap-1.5" "sm:text-sm" "sm:leading-6" "hover:bg-slate-200"]
                    (when (= idx @!selected)
                      ["bg-slate-200"]))
            :title (:filter.omnibox/tooltip the-filter)
            :on-pointer-down #(on-row-click % row opts)}
       (case type
         :row.type/empty
         [:span.text-slate-400 (tr "No matches")]

         :row.type/filter
         [:<>
          [:span.flex.mt-1 [:div.w-4.h-4.text-slate-400 [icon-filter]]]
          (filter-text the-filter)]

         :row.type/choice
         [:<>
          [:span.flex.mt-1
           [checkbox {:checked? checked?
                      :disabled? disabled?}]]
          [:span {:class (when disabled? ["text-gray-300"])}
           (if date?
             (date-label label)
             label)]])]))])

(def number-regex
  #"[+-]?\d+(.\d+)?([eE][+-]?\d+)?")

(defn omnibox [{:keys [choices filters set-filters font keyword? numeric? date?] :as opts}]
  (r/with-let [!text       (r/atom "")
               !expanded   (r/atom false)
               !selected   (r/atom nil)
               !tick       (atom 0)]
    (let [{:filter.omnibox.type/keys [exact substring glob range not-substring not-glob]} (group-by :filter.omnibox/type filters)
          filter-from-text (cond
                             numeric?
                             (text->numeric-filter @!text)
                             date?
                             (text->date-filter @!text)
                             keyword?
                             (text->filter (name @!text))
                             (:text->filter opts) ((:text->filter opts) @!text)
                             :else
                             (text->filter @!text))
          value+normalized (cond->> choices
                             true (map #(vector % (normalize %)))
                             (map? filter-from-text) (filter (fn [[_ normalized]] (matches-filter? normalized filter-from-text)))
                             #_#_true             (sort-by second))
          rows             (if (empty? value+normalized)
                             [{:row/type      :row.type/empty
                               :row/disabled? true}]
                             (concat
                              (when filter-from-text
                                [{:row/type       :row.type/filter
                                  :row/the-filter filter-from-text}])
                              (when date?
                                (let [today (.startOf (.now DateTime) "day")]
                                  [{:row/type :row.type/filter
                                    :row/the-filter {:filter.omnibox/type :filter.omnibox.type/exact
                                                     :filter.omnibox/custom-label (tr "Today")
                                                     :filter.omnibox/normalized-value (.toJSDate today)}}
                                   {:row/type :row.type/filter
                                    :row/the-filter {:filter.omnibox/type :filter.omnibox.type/exact
                                                     :filter.omnibox/custom-label (tr "Tomorrow")
                                                     :filter.omnibox/normalized-value (.toJSDate (.plus today #js {:days 1}))}}
                                   {:row/type :row.type/filter
                                    :row/the-filter {:filter.omnibox/type :filter.omnibox.type/range
                                                     :filter.omnibox/custom-label (tr "Next %1 days" 7)
                                                     :filter.omnibox/from (.toJSDate today)
                                                     :filter.omnibox/to (.toJSDate (.plus today #js {:days 7}))}}
                                   {:row/type :row.type/filter
                                    :row/the-filter {:filter.omnibox/type :filter.omnibox.type/exact
                                                     :filter.omnibox/custom-label (tr "Yesterday")
                                                     :filter.omnibox/normalized-value (.toJSDate (.minus today #js {:days 1}))}}
                                   {:row/type :row.type/filter
                                    :row/the-filter {:filter.omnibox/type :filter.omnibox.type/range
                                                     :filter.omnibox/custom-label (tr "Past %1 days" 7)
                                                     :filter.omnibox/from (.toJSDate (.minus today #js {:days 7}))
                                                     :filter.omnibox/to (.toJSDate today)}}]))
                              (for [[value normalized] value+normalized
                                    :let [checked?  (and
                                                     (not (empty? (concat exact substring glob range)))
                                                     (matches? normalized filters))
                                          disabled? (or
                                                     (any? #(matches-filter? normalized %) substring)
                                                     (any? #(matches-filter? normalized %) glob)
                                                     (any? #(matches-filter? normalized %) range)
                                                     (any? #(matches-filter? normalized %) not-substring)
                                                     (any? #(matches-filter? normalized %) not-glob))]]
                                {:row/type             :row.type/choice
                                 :row/label            value
                                 :row/normalized-value normalized
                                 :row/checked?         checked?
                                 :row/disabled?        disabled?})))
          opts             (assoc opts
                                  :rows        rows
                                  :!text       !text
                                  :!expanded   !expanded
                                  :!selected   !selected
                                  :!tick       !tick
                                  :keyword?    keyword?
                                  :numeric?    numeric?
                                  :date?       date?)]
      [:div
       {:class ["flex" "flex-col" "gap-1"]}
       [popup
        (swap! !tick inc)
        {:button-classes ["w-full"]
         :!expanded !expanded
         :on-close  (fn [_]
                      (reset! !text "")
                      (reset! !selected nil))}
        [input opts]
        [dropdown opts]]
       [selected-filters opts]])))
